import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Typography,
} from '@mui/material';

import { BackButton, PageTitle } from '../../../../components/common';
import ButtonContainer from '../../../../components/ButtonContainer';
import EditIcon from '../../../../icons/EditIcon';

import FileList from '../UploadDocument/FileList';
import {
  StyledCardContentLine,
  StyledCardHeader,
  StyledVerticalContent,
} from './SubmitClaimForm.styled';

const renderAnchorTag = (chunk, href) => {
  return (
    <Link variant="p" href={href} target="_blank" rel="noopener noreferrer">
      {chunk}
    </Link>
  );
};

const SubmitClaimForm = ({
  selectedDocuments,
  backButtonLink,
  editDetailsButtonLink,
  editUploadedFilesLink,
  pageTitleId,
  formDataCardHeaderId,
  cardContent,
  uploadedFilesCardHeaderId,
  uploadClaimForm,
  submitButtonId,
  dataProtectionLink,
}) => {
  const history = useHistory();

  const [disabled, setDisabled] = useState(false);

  const [confirmDataProcessing, setConfirmDataProcessing] = useState(false);

  const validSelectedDocuments = Array.isArray(selectedDocuments)
    ? selectedDocuments.filter((d) => d?.valid)
    : [];

  const cardContentToDisplay = Array.isArray(cardContent)
    ? cardContent.filter((contentItem) => !!contentItem?.value)
    : [];

  const handleButtonClick = (event) => {
    event.preventDefault();

    if (typeof uploadClaimForm === 'function' && confirmDataProcessing) {
      setDisabled(true);
      uploadClaimForm();
    }
  };

  return (
    <>
      <BackButton to={backButtonLink}>
        <Typography variant="h3">
          <FormattedMessage id="common.button.back" />
        </Typography>
      </BackButton>

      <PageTitle>
        <Typography variant="h1">
          <FormattedMessage id={pageTitleId} />
        </Typography>
      </PageTitle>

      <form onSubmit={handleButtonClick}>
        <StyledVerticalContent>
          <Paper>
            <StyledCardHeader>
              <Typography variant="h1">
                <FormattedMessage id={formDataCardHeaderId} />
              </Typography>

              {editDetailsButtonLink && (
                <IconButton
                  variant="round"
                  onClick={() => history.push(editDetailsButtonLink)}
                  aria-label="edit data"
                >
                  <EditIcon />
                </IconButton>
              )}
            </StyledCardHeader>

            {cardContentToDisplay.map((contentItem) => {
              const { label, value } = contentItem;
              return (
                <StyledCardContentLine key={label}>
                  <Typography variant="p" color="professional_blue">
                    <FormattedMessage id={label} />
                  </Typography>
                  <Typography variant="h3">&nbsp;{value}</Typography>
                </StyledCardContentLine>
              );
            })}
          </Paper>

          <Paper>
            <StyledCardHeader>
              <Typography variant="h1">
                <FormattedMessage id={uploadedFilesCardHeaderId} />
              </Typography>

              <IconButton
                variant="round"
                onClick={() => history.push(editUploadedFilesLink)}
                aria-label="edit uploaded files"
              >
                <EditIcon />
              </IconButton>
            </StyledCardHeader>

            <div>
              <FileList files={validSelectedDocuments} />
            </div>
          </Paper>

          <FormControlLabel
            control={
              <Checkbox
                id="data-protection-checkbox"
                name="data-protection-checkbox"
                checked={confirmDataProcessing}
                onChange={(event) => setConfirmDataProcessing(event.target.checked)}
                slotProps={{
                  input: {
                    'aria-label': 'data protection',
                    'aria-checked': confirmDataProcessing ? 'true' : 'false',
                  },
                }}
              />
            }
            label={
              <Typography variant="p">
                <FormattedMessage
                  id="new_claim.submit_claim_form.data_protection_checkbox"
                  values={{
                    a: (chunk) => renderAnchorTag(chunk, dataProtectionLink),
                  }}
                />
              </Typography>
            }
            required
          />
        </StyledVerticalContent>

        <ButtonContainer>
          <Button
            type="submit"
            data-testid="btn_submit_claim"
            disabled={disabled || !validSelectedDocuments.length}
          >
            <Typography variant="buttons">
              <FormattedMessage id={submitButtonId} />
            </Typography>
          </Button>
        </ButtonContainer>
      </form>
    </>
  );
};

export default SubmitClaimForm;
